import { useAppSelector } from '@/redux/hooks'
import { MediaImageIcon } from '@/ui/icons/media-image'
import {
  ReturnsNonReturnableItemReasonEnum,
  type ReturnsLineItem
} from '@itsrever/rever-api'
import { useTranslation } from 'react-i18next'
import { useTranslate } from '@/hooks'
import { calculateUnitPrice } from '../helpers/calculate-unit-price'

const getNonReturnableReasonIndex = (
  reason: (typeof ReturnsNonReturnableItemReasonEnum)[keyof typeof ReturnsNonReturnableItemReasonEnum]
) => {
  return Object.values(ReturnsNonReturnableItemReasonEnum).indexOf(reason)
}

interface NonReturnableItemCardProps {
  item: ReturnsLineItem
}

export const NonReturnableItemCard = ({ item }: NonReturnableItemCardProps) => {
  const { i18n } = useTranslation()

  const { translate } = useTranslate()

  const group = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const isHoff = group === 'hoff'
  const isMisako = group === 'misako'

  const moneyFormat = useAppSelector(
    (store) => store.returnsApi.order.response?.order?.money_format
  )

  const price = calculateUnitPrice(
    { quantity: item.quantity, total: item.total },
    { amount_multiplied_by: moneyFormat?.amount_multiplied_by }
  )

  const formattedPrice = price.toLocaleString(i18n.language, {
    style: 'currency',
    currency: moneyFormat?.currency
  })

  const image = item.product?.images?.[0]

  const reason = item.non_returnable_items?.[0]?.reason
  const nonReturnableReason = reason
    ? translate(`item_selection_reason${getNonReturnableReasonIndex(reason)}`)
    : 'Non returnable'

  if (isHoff) {
    return (
      <div className="relative border-t-1 border-solid border-black p-2 pr-10 sm:pr-11">
        <div className="flex w-full flex-row items-center gap-2 opacity-60 sm:gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
            <div className="relative my-2 h-24 w-16 shrink-0 overflow-hidden rounded-lg sm:h-28 sm:w-20 md:h-32 md:w-24">
              {image ? (
                <img
                  src={image.src}
                  alt={image.alt ?? item.name ?? 'Product'}
                  className="absolute inset-0 size-full rounded-md object-cover"
                />
              ) : (
                <div className="flex size-full items-center justify-center bg-neutral-50 text-neutral-500">
                  <MediaImageIcon width={20} height={20} />
                </div>
              )}
            </div>
            <div className="flex min-w-0 flex-col justify-start gap-1.5 sm:gap-3">
              <p className="line-clamp-2 text-xs font-normal uppercase text-neutral-800">
                {item.name}
              </p>
              <p className="truncate text-xs font-light text-black">
                {item.variant_name}
              </p>
            </div>
          </div>
          <div className="w-6 shrink-0 text-center sm:w-10">
            <p className="text-sm font-normal">{item.quantity}</p>
          </div>
          <div className="w-16 shrink-0 text-right sm:w-24">
            <p className="whitespace-nowrap text-sm font-normal">
              {formattedPrice}
            </p>
          </div>
        </div>
        <p
          className="absolute bottom-3 right-1 mr-1 mt-2 rounded-lg border border-solid border-neutral-500 bg-white px-3 py-1 text-right text-xs text-neutral-500"
          dangerouslySetInnerHTML={{ __html: nonReturnableReason }}
        />
      </div>
    )
  }

  if (isMisako) {
    return (
      <div className="flex flex-wrap items-end justify-between gap-4 rounded-lg bg-white p-2">
        <div className="flex w-full flex-col gap-4 opacity-60">
          <div className="relative h-48 w-36 overflow-hidden md:h-72 md:w-60">
            {image ? (
              <img
                src={image.src}
                width={640}
                height={800}
                alt={image.alt ?? item.name ?? 'Product'}
                className="absolute inset-0 size-full object-cover"
              />
            ) : (
              <div className="flex size-full items-center justify-center bg-neutral-50 text-neutral-500">
                <MediaImageIcon width={24} height={24} />
              </div>
            )}
          </div>
          <div className="flex max-w-36 flex-col gap-0.5 md:max-w-60">
            <div className="flex w-full flex-col flex-wrap justify-between gap-2 md:flex-row">
              <div className="space-y-1">
                <p className="line-clamp-2 text-xs font-light leading-normal text-neutral-800 md:max-w-44">
                  {item.name}
                </p>
                <p className="text-xs font-light text-neutral-800">
                  {item.variant_name} - {item.product?.sku}
                </p>
              </div>
              <p className="text-xs font-light text-neutral-800">
                {formattedPrice}
              </p>
            </div>
            <p className="mt-1 text-xs font-light text-neutral-800">
              Cantidad {item.quantity}
            </p>
          </div>
        </div>
        <p
          className="mr-auto w-fit max-w-96 shrink-0 rounded-xl border border-solid border-yellow-500 bg-white px-3 py-1 text-right text-xs text-yellow-500"
          dangerouslySetInnerHTML={{ __html: nonReturnableReason }}
        />
      </div>
    )
  }

  return (
    <div className="relative rounded-xl bg-white p-2.5 shadow-sm transition-shadow">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="flex gap-3 opacity-60">
          <div className="relative size-[5.5rem] shrink-0 overflow-hidden rounded-md">
            {image ? (
              <img
                height={88}
                width={88}
                src={image.src}
                alt={image.alt}
                className="size-full object-cover"
              />
            ) : (
              <div className="flex size-full min-h-0 items-center justify-center bg-neutral-50 text-neutral-500">
                <MediaImageIcon width={20} height={20} />
              </div>
            )}
            {item.quantity && item.quantity > 1 ? (
              <span className="absolute bottom-1 right-1 rounded-sm bg-white/50 p-0.5 px-1 text-xs font-medium backdrop-blur-sm">
                x {item.quantity}
              </span>
            ) : null}
          </div>
          <div className="flex flex-col py-1">
            <p className="line-clamp-2 text-left text-sm font-normal">
              {item.name}
            </p>
            <p className="text-xs font-normal text-neutral-500">
              {item.variant_name}
            </p>
            <p className="mt-auto text-sm font-normal">{formattedPrice}</p>
          </div>
        </div>
        <div className="ml-auto">
          <p
            className="rounded-md border border-solid border-yellow-700 px-2 py-1 text-xs text-yellow-700"
            dangerouslySetInnerHTML={{ __html: nonReturnableReason }}
          />
        </div>
      </div>
    </div>
  )
}
